export type CompressImageOptions = {
  maxKB?: number;
  minQuality?: number;
  initialQuality?: number;
  scaleStep?: number;
  maxPasses?: number;
  outputType?: 'image/jpeg' | 'image/webp';
};

export const DEFAULT_IMAGE_MAX_KB = 200;
export const HOTEL_IMAGE_MAX_KB = 500;

const ENTITY_TYPE_HOTEL = 5;

export const getImageMaxKBForEntityType = (entityType?: number): number => {
  if (entityType === ENTITY_TYPE_HOTEL) return HOTEL_IMAGE_MAX_KB;
  return DEFAULT_IMAGE_MAX_KB;
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));

export const compressImageIfNeeded = async (file: File, options: CompressImageOptions = {}): Promise<File> => {
  const {
    maxKB = DEFAULT_IMAGE_MAX_KB,
    minQuality = 0.45,
    initialQuality = 0.86,
    scaleStep = 0.86,
    maxPasses = 10,
    outputType = 'image/jpeg',
  } = options;

  if (!file.type.startsWith('image/')) return file;

  const maxBytes = maxKB * 1024;
  if (file.size <= maxBytes) return file;

  try {
    const dataUrl = await fileToDataUrl(file);
    const image = new Image();

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = reject;
      image.src = dataUrl;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    let scale = 1;
    let bestBlob: Blob | null = null;

    for (let pass = 0; pass < maxPasses; pass++) {
      canvas.width = Math.max(1, Math.floor(image.width * scale));
      canvas.height = Math.max(1, Math.floor(image.height * scale));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      let quality = initialQuality;
      for (let step = 0; step < 9; step++) {
        const blob = await canvasToBlob(canvas, outputType, quality);
        if (!blob) continue;

        if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
        if (blob.size <= maxBytes) {
          const baseName = file.name.replace(/\.[^/.]+$/, '');
          const ext = outputType === 'image/webp' ? 'webp' : 'jpg';
          return new File([blob], `${baseName}.${ext}`, { type: outputType });
        }

        quality -= 0.08;
        if (quality < minQuality) break;
      }

      scale *= scaleStep;
    }

    if (bestBlob && bestBlob.size < file.size) {
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const ext = outputType === 'image/webp' ? 'webp' : 'jpg';
      return new File([bestBlob], `${baseName}.${ext}`, { type: outputType });
    }
  } catch {
    return file;
  }

  return file;
};

export const compressImagesIfNeeded = async (files: File[], options: CompressImageOptions = {}): Promise<File[]> => {
  if (!files?.length) return [];
  return Promise.all(files.map((f) => compressImageIfNeeded(f, options)));
};

export const compressImageForEntityType = async (
  file: File,
  entityType?: number,
  options: CompressImageOptions = {},
): Promise<File> =>
  compressImageIfNeeded(file, {
    ...options,
    maxKB: options.maxKB ?? getImageMaxKBForEntityType(entityType),
  });

export const compressImagesForEntityType = async (
  files: File[],
  entityType?: number,
  options: CompressImageOptions = {},
): Promise<File[]> => {
  if (!files?.length) return [];
  return Promise.all(files.map((file) => compressImageForEntityType(file, entityType, options)));
};

